import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  alpha,
  useTheme,
} from "@mui/material";
import { People as PeopleIcon } from "@mui/icons-material";
import { apiRequest } from "../config/api";
import { getBracketNames } from "../utils/bracketNames";
import { getRoundLabel } from "../utils/roundLabels";
import { useOrganization } from "../contexts/OrganizationContext";

interface MatchEntry {
  id: string;
  stage: "group" | "knockout" | "americana";
  groupName?: string;
  round?: string;
  roundLabel?: string;
  roundNumber?: number;
  knockoutType?: "masters-cup" | "tour-cup";
  isConsolationMatch?: boolean;
  partnerId?: string;
  partnerName?: string;
  opponent1Id: string | null;
  opponent1Name: string;
  opponent2Id: string | null;
  opponent2Name: string;
  teamGames: number;
  opponentGames: number;
  won: boolean | null;
  startTime?: string;
  courtNumber?: number;
}

interface MatchBreakdownResponse {
  tournamentId: string;
  tournamentName: string;
  teamId: string;
  partnerId: string;
  partnerName: string;
  matches: MatchEntry[];
}

interface TournamentMatchBreakdownProps {
  playerId: string;
  tournamentId: string;
}

const TournamentMatchBreakdown: React.FC<TournamentMatchBreakdownProps> = ({
  playerId,
  tournamentId,
}) => {
  const theme = useTheme();
  const { currentOrg } = useOrganization();
  const bracketNames = getBracketNames(currentOrg?.slug);
  const [data, setData] = useState<MatchBreakdownResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchMatches = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiRequest(
          `/api/players/${playerId}/tournament-history/${tournamentId}/matches`
        );
        if (!response.ok) {
          throw new Error("Failed to load match breakdown");
        }
        const json = await response.json();
        if (!cancelled) {
          setData(json);
        }
      } catch (err) {
        console.error("Error fetching tournament match breakdown:", err);
        if (!cancelled) {
          setError("Could not load match details for this tournament");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchMatches();

    return () => {
      cancelled = true;
    };
  }, [playerId, tournamentId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data || data.matches.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No match-level data recorded for this tournament.
      </Typography>
    );
  }

  const sectionLabel = (match: MatchEntry) => {
    if (match.stage === "group") {
      return match.groupName ? `Group Stage — ${match.groupName}` : "Group Stage";
    }
    if (match.stage === "americana") {
      return `Round ${match.roundNumber}`;
    }
    const cupLabel =
      match.knockoutType === "masters-cup" ? bracketNames.primary : bracketNames.secondary;
    const round = match.roundLabel || getRoundLabel(match.round || "");
    return `${cupLabel} — ${round}`;
  };

  const isAmericana = data.matches.length > 0 && data.matches.every((m) => m.stage === "americana");

  const sections = new Map<string, MatchEntry[]>();
  data.matches.forEach((match) => {
    const key = sectionLabel(match);
    if (!sections.has(key)) sections.set(key, []);
    sections.get(key)!.push(match);
  });

  return (
    <Box>
      {!isAmericana && (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            px: 1.5,
            py: 0.75,
            borderRadius: 99,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
          }}
        >
          <PeopleIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            With {data.partnerName}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {Array.from(sections.entries()).map(([section, matches]) => (
          <Box key={section}>
            <Typography
              variant="caption"
              sx={{ fontWeight: "bold", color: "text.secondary", textTransform: "uppercase" }}
            >
              {section}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 0.5 }}>
              {matches.map((match) => {
                const isDraw = match.won === null;
                const accentColor = isDraw
                  ? theme.palette.grey[500]
                  : match.won
                  ? theme.palette.success.main
                  : theme.palette.error.main;

                return (
                  <Paper
                    key={match.id}
                    variant="outlined"
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      borderLeft: `4px solid ${accentColor}`,
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": { transform: "translateY(-2px)", boxShadow: 2 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          label={isDraw ? "Draw" : match.won ? "Won" : "Lost"}
                          size="small"
                          sx={{
                            fontWeight: "bold",
                            bgcolor: alpha(accentColor, 0.15),
                            color: accentColor,
                          }}
                        />
                        {match.isConsolationMatch && (
                          <Chip label="Consolation" size="small" variant="outlined" />
                        )}
                      </Box>
                      {match.courtNumber && (
                        <Typography variant="caption" color="text.secondary">
                          Court {match.courtNumber}
                        </Typography>
                      )}
                    </Box>
                    {match.stage === "americana" && match.partnerName && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        With {match.partnerName}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
                        vs {match.opponent1Name} & {match.opponent2Name}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: accentColor, flexShrink: 0 }}
                      >
                        {match.teamGames}-{match.opponentGames}
                      </Typography>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TournamentMatchBreakdown;
